import { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Info, AlertTriangle, CheckCircle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const typeStyles = {
    success: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10' },
    info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10' },
};

export function NotificationBell() {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    const fetchNotifications = async () => {
        try {
            const [nRes, cRes] = await Promise.all([
                fetch(`${API}/api/operator/notifications`, { credentials: 'include' }),
                fetch(`${API}/api/operator/notifications/unread-count`, { credentials: 'include' })
            ]);
            if (nRes.ok) setNotifications(await nRes.json());
            if (cRes.ok) { const d = await cRes.json(); setUnreadCount(d.count); }
        } catch {}
    };

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const markRead = async (id) => {
        await fetch(`${API}/api/operator/notifications/${id}/read`, { method: 'PUT', credentials: 'include' });
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
    };

    const markAllRead = async () => {
        await fetch(`${API}/api/operator/notifications/read-all`, { method: 'PUT', credentials: 'include' });
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
    };

    const handleClick = (notif) => {
        if (!notif.is_read) markRead(notif.id);
        if (notif.link) { navigate(notif.link); setOpen(false); }
    };

    const timeAgo = (dateStr) => {
        const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen(!open)}
                className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
                data-testid="notification-bell"
            >
                <Bell className={cn("w-5 h-5", unreadCount > 0 ? "text-gold" : "text-muted-foreground")} />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-1 flex items-center justify-center rounded-full bg-gold text-[#0A0D14] text-[10px] font-bold" data-testid="unread-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-12 w-80 sm:w-96 bg-[#121620] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <h3 className="text-sm font-semibold text-white">Notifications</h3>
                        {unreadCount > 0 && (
                            <button onClick={markAllRead} className="text-xs text-gold hover:text-gold/80 flex items-center gap-1 transition-colors" data-testid="mark-all-read">
                                <CheckCheck className="w-3 h-3" /> Mark all read
                            </button>
                        )}
                    </div>

                    {/* List */}
                    <div className="max-h-[360px] overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="py-10 text-center">
                                <Bell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                                <p className="text-sm text-muted-foreground">No notifications yet</p>
                            </div>
                        ) : (
                            notifications.map(n => {
                                const style = typeStyles[n.type] || typeStyles.info;
                                const Icon = style.icon;
                                return (
                                    <button
                                        key={n.id}
                                        onClick={() => handleClick(n)}
                                        className={cn(
                                            "w-full text-left px-4 py-3 border-b border-white/5 flex items-start gap-3 transition-colors hover:bg-white/5",
                                            !n.is_read && "bg-white/[0.02]"
                                        )}
                                        data-testid={`notification-${n.id}`}
                                    >
                                        <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5", style.bg)}>
                                            <Icon className={cn("w-4 h-4", style.color)} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={cn("text-sm font-medium truncate", n.is_read ? "text-white/70" : "text-white")}>{n.title}</p>
                                                {!n.is_read && <div className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                                            <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                                        </div>
                                        {n.link && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0 mt-1" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
